/**
 * Skill-Agent 入口 — 跨层（core 生产 ↔ renderer 消费）契约的单一真相源
 * （agent-entity-architecture-spec §2「Skill-Agent 卡片」/ W3）。
 *
 * 【产品白话】主页除了「能聊的角色 Agent」，还多一排「一键干活卡片」：一张卡片 =
 * 一个技能（拉飞书日报 / 翻微信群 / 拆竞品…）。点一下就开一个只带这一件事所需
 * 工具的轻量会话（~7K），不实例化 40K 的满配主会话——省钱、直达、不闲聊。
 *
 * 【机制】不为 116 个技能各造一个硬编码 agent（红线 #7）。而是：一个技能被登记为
 * 「miniagent 直达」并归入一个「簇 profile」→ 卡片被点击时，用一个通用启动器把
 * (技能 × 簇 profile) 动态组装成 lean SessionProfile，走既有 applySessionProfile
 * 单一消费点起会话。簇定义 + 技能→簇映射在 core（packages/core/src/data/
 * skill-agent-launcher.ts）；本文件只放**跨进程要对齐的字符串契约 + 展示型**。
 *
 * 载体形态 = 复用「按 chat.agentId 命中 profile」的 applyChatAgent seam（同 #1386
 * BUILTIN_AGENT_RUNTIMES 载体1）：卡片启动 = 建一个 `agentId = capabilityChatBinding
 * (buildCapabilityRef(...))` 的 chat（v3 capabilityRef scheme）；core 的 applyChatAgent 用
 * chatAgentIdToCapabilityRef 归一化后 resolveProfile → 现拼 lean profile。未归任何能力 →
 * 无卡片、无绑定 → 原样走满配主会话（零破坏回退）。
 */
import { CODEX_CAPABILITY_REF } from "./types/index.js";
/**
 * 簇 profile 的稳定 id（单一真相源，有序——卡片分组按此顺序渲染）。core 的簇定义
 * SKILL_AGENT_CLUSTERS 以此为 key 全覆盖，缺一编译期即报（Record 完整性兜底）。
 *
 * 簇 = 插件（L5 80 技能 / 13 插件盘点结论：插件是簇的天然单元）。成员靠 SkillSelector
 * （通常 `{plugin:"X"}`）对 live 已发现技能展开——org 往某插件加新技能，零代码自动进
 * 簇白名单（红线 #7：加技能 = 改数据不改码）。
 */
export const SKILL_AGENT_CLUSTER_IDS = [
    "feishu-connector",
    "wechat-connector",
    "dingtalk-connector",
    "tencent-docs-connector",
    "content-teardown",
    "content-research",
    "content-strategy",
    "ad-ops",
    "hr-kit",
    "research",
    "meeting-intel",
    "projects",
    "image-craft",
];
/**
 * SDK-native 工具里「默认对 lean 会话关掉、按需放开」的**可门控集** —— `SessionProfile
 * .nativeTools` 白名单的语义定义域（红线 #9 单一真相源）。作者面声明「我要放开哪几个」，
 * 唯一消费点 applySessionProfile 把 `本集 − nativeTools` 折成 disallowedTools 硬禁。
 *
 * 住 shared 而非 core/skill-agent-launcher 的原因：`nativeTools` 是 SessionProfile 的字段，
 * 它的取值域必须与字段定义同层，否则每个新消费者（簇启动器 / AgentProfile / 未来载体）
 * 各抄一份取反逻辑 = 第二真相源。
 *
 * Read/Glob/Grep/TodoWrite/TaskCreate/TaskUpdate 属安全只读/记事，恒开不入本集（见 LEAN_AGENT_BASELINE_TOOLS）；
 * `Skill` **绝不**入本集（miniagent 的全部意义就是调技能，禁掉 Skill 等于阉了自己）。
 */
export const LEAN_GATEABLE_NATIVE_TOOLS = [
    "Bash",
    "Write",
    "Edit",
    "MultiEdit",
    "NotebookEdit",
    "WebSearch",
    "WebFetch",
    "Task",
    "Agent",
    "KillShell",
];
/**
 * 命令行工具家族（B-97）。DOSIA 所有工具门控词汇里的 `Bash` 指「这个会话能不能执行命令」，
 * 但 Claude 腿的内置 CLI 有两个命令行工具：Windows 上找不到 git-bash 时不注册 `Bash`，
 * 改挂 `PowerShell` 工具（锁定 SDK 0.3.282 内置 `claude` 实读：注册判据 `ZS()` ——
 * 非 Windows 只认 `CLAUDE_CODE_USE_POWERSHELL_TOOL`，Windows 上找不到 git-bash 即开）。
 * CLI 的 `Bash` 整工具禁令**不连带** PowerShell（`PowerShellTool` 定义里没有
 * `familyParentToolName`，拒绝规则按工具名逐个匹配），所以只禁 `Bash` 的会话在没装
 * git-bash 的 Windows 员工机上会拿到完整 PowerShell。
 *
 * 键 = DOSIA 门控词汇里的工具名，值 = 它在 CLI 上的同家族工具。只有**整工具**名
 * （裸名）按家族展开；`Bash(rm:*)` 这类带参数的是 bash 语法的权限规则，不翻译。
 * 两个方向都走这张表：禁令侧（B-97，`disallowedTools` 写 Bash 就连带 PowerShell）与
 * 白名单侧（B-102，子 agent `tools` 写 Bash 就同时放 PowerShell——穷举白名单里不列就没有，
 * 只写 Bash 的后台技能助手在无 git-bash 的 Windows 上是零个命令行）。满配会话不经白名单，
 * 没禁 `Bash` 的照样有 PowerShell。pi 腿同一条二选一（N-99：Windows 无 git-bash 时注册
 * PowerShell 而不是跑 cmd 的 Bash），PiBackend 构造期与主 Agent 工具策略、Task 具名 agent 收窄
 * 同样经本表展开。
 */
export const COMMAND_LINE_TOOL_FAMILY = {
    Bash: ["PowerShell"],
};
/**
 * 命令行家族的全部成员名（家族头在前）。B-101：命令行护栏（危险命令地板、Chrome 进程守卫、
 * 飞书配置拦截、lark-cli 回喂、canUseTool 的危险命令分支、风险分级）的判据面此前都是
 * 「工具名 === Bash」，PowerShell 一条都不触发；这些判据按本函数的返回值挂接 / 分流，
 * 家族表仍是唯一单源。
 */
export function commandLineToolFamilyNames(head = "Bash") {
    return [head, ...(COMMAND_LINE_TOOL_FAMILY[head] ?? [])];
}
/**
 * 这个工具名是不是命令行家族的一员。**大小写不敏感**：Claude 腿 hook 收到的是 CLI 原名
 * （`Bash` / `PowerShell`），`resolvePermission` 收到的是 `toolName.toLowerCase()`，
 * 风险登记表的键也是小写——三处都用本函数判，不各写一份名单。
 */
export function isCommandLineToolName(name) {
    if (typeof name !== "string")
        return false;
    const lower = name.toLowerCase();
    return Object.entries(COMMAND_LINE_TOOL_FAMILY).some(([head, members]) => head.toLowerCase() === lower || members.some((m) => m.toLowerCase() === lower));
}
/**
 * 把一份整工具名单按 {@link COMMAND_LINE_TOOL_FAMILY} 补齐家族成员（唯一实现；禁令与白名单
 * 共用——两侧语义都是「写了家族头就意味着整个家族」）。
 * 返回新数组：输入顺序保留、只在末尾追加缺的成员，别的项原样不动（不去重、不排序）。
 */
export function withCommandLineFamily(disallowed) {
    const present = new Set(disallowed);
    const missing = [];
    for (const [head, members] of Object.entries(COMMAND_LINE_TOOL_FAMILY)) {
        if (!present.has(head))
            continue;
        for (const member of members) {
            if (!present.has(member) && !missing.includes(member))
                missing.push(member);
        }
    }
    return [...disallowed, ...missing];
}
/**
 * SDK-native「恒开安全底座」= 只读检索 + 记事（LEAN_GATEABLE_NATIVE_TOOLS 的互补集：
 * 这些永不门控，`nativeTools` 无论声明成什么都不影响它们）。lean 会话靠 disallowedTools
 * 减去门控项后天然留下它们；派发子 agent 走 SDK `tools` 白名单（claude-code
 * resolveAgentTools 范式）时白名单是穷举式 —— 故这里把恒开底座显式列出作为白名单起点。
 * `Skill` **不入**（SDK 弃用 `tools` 里传 Skill，走 AgentDefinition.skills 字段）。
 */
export const LEAN_AGENT_BASELINE_TOOLS = [
    "Read",
    "Glob",
    "Grep",
    // 本表每个名字都得是随包 CLI 的内置工具名或其旧名（sdk-0-3-310-adapt.test.ts 以 SDK 导出的名单钉住；LS 2026-10-26 删，N-168）。
    "TodoWrite",
    // SDK 0.3.233+: TaskCreate family replaces TodoWrite as the 记事 write path
    // on Opus 4.8 / Sonnet 5 / Fable 5. Keep both so older models still have
    // TodoWrite and newer models still have TaskCreate. TaskList/TaskGet stay
    // out of this baseline — they are keep-alive'd on the main session via
    // SDK_TODO_TRACKING_TOOLS / allowedTools, and REQ-53 still excludes them
    // from child agents.
    "TaskCreate",
    "TaskUpdate",
];
/**
 * SDK 0.3.233+ todo/task-tracking tools. On Opus 4.8 / Sonnet 5 / Fable 5 /
 * Mythos 5 and newer these are no longer in the default tool surface; the SDK
 * keeps them only if they appear in `options.tools` or `options.allowedTools`
 * (or `CLAUDE_CODE_ENABLE_TODO_TOOLS=1`). DOSIA already treats TodoWrite as
 * 记事底座；本常量是 TaskCreate 家族的 keep-alive 清单，由
 * `buildBaseAllowedTools` 注入主会话 `allowedTools`。
 *
 * TaskList / TaskGet 与 REQ-53 子会话排除清单同名：主会话要保活记事面，子会话
 * 仍按父侧编排控制面排除（session-tool-surface SUBAGENT_TOOL_SURFACE_EXCLUSIONS）
 * ——两份清单语义不同，禁止合并。
 */
export const SDK_TODO_TRACKING_TOOLS = [
    "TaskCreate",
    "TaskGet",
    "TaskUpdate",
    "TaskList",
];
/**
 * 满配 / cron / 云任务会话的**内置文件·shell 面** —— 不应被会话类型神秘抽走。
 *
 * 与 lean 会话的 `nativeTools` 白名单刻意区分：lean 可按簇收窄可门控集；本常量是
 * 「普通主会话 / 无人值守 / agent-host 云 turn」在 options 构建后**仍应可用**的
 * 内置面验收清单（红线 #10 单一真相源）。
 *
 * - 恒开底座（Read/Glob/Grep/TodoWrite/TaskCreate/TaskUpdate）永不进 disallowedTools（applySessionProfile）
 * - Bash/Write/Edit 是可门控集成员，仅当 SessionProfile.nativeTools 显式收窄时才禁
 * - PowerShell = Windows 原生 shell 工具名（Claude CLI 在 win32 注册）；与 Bash 并列验收
 *
 * 住 shared：与 LEAN_* 同层，core options builder / agent-host / vitest 共用，禁各抄一份。
 */
export const FULL_SESSION_BUILTIN_TOOLS = [
    ...LEAN_AGENT_BASELINE_TOOLS,
    "Bash",
    "Write",
    "Edit",
    "PowerShell",
];
/**
 * 「未声明能力面的 AgentProfile」落的**最小能力面** —— 五轴全 `[]`，即只剩恒开只读底座
 * （{@link LEAN_AGENT_BASELINE_TOOLS}：Read/Glob/Grep/TodoWrite/TaskCreate/TaskUpdate）+ Skill 工具本身。
 *
 * 产品语义（用户拍板 2026-07-19）：mini agent = **只加入本 agent 需要的能力（白名单从零）**，
 * 不是「全量减一两个」。推论：一个能力面「继承全部」的 AgentProfile 没有存在理由——需要
 * 大而全就该直接用主会话。故 `AgentProfile.capabilities === undefined` 的兜底**不是**
 * 「不覆写（继承全部）」，而是本常量。
 *
 * 与 {@link SessionProfile} 各轴的 `undefined`（= 该轴不覆写）刻意区分：这里每一轴都是
 * 显式 `[]`（= 该轴全砍）。`memory` 轴刻意**不**在本常量里表态（它不属于能力面四轴，
 * 载体自己决定，见 session-manager applyChatAgent 的兜底分支注释）。
 *
 * 住 shared 而非 core：与 LEAN_* 两个常量同层（都是 SessionProfile 字段的取值域/基线定义），
 * 否则每个新载体各抄一份「五轴全 []」= 第二真相源。
 */
export const MINI_AGENT_BASELINE = Object.freeze({
    skills: [],
    dosiaTools: [],
    mcpServers: [],
    nativeTools: [],
    subagents: [],
});
/** {@link SessionProfile} 的字符串数组轴 —— coerceSessionProfile 的清洗面。 */
const SESSION_PROFILE_ARRAY_AXES = [
    "skills",
    "dosiaTools",
    "mcpServers",
    "nativeTools",
    "subagents",
    "disallowedTools",
];
function cleanNameList(raw) {
    const out = [];
    for (const v of raw) {
        if (typeof v !== "string")
            continue;
        const t = v.trim();
        if (t && !out.includes(t))
            out.push(t);
    }
    return out;
}
/**
 * 把一个不可信值（sqlite 列 / manifest JSON / IPC payload）收成 {@link SessionProfile}
 * —— **唯一**的 SessionProfile 反序列化整形器（红线 #9：两个 producer/consumer 面
 * ——桌面 sqlite `agents.capabilities` 与 org 发布 manifest —— 共用它，禁各自手搓）。
 *
 * 刻意只做**容器级**防御：
 *   - JSON 字符串先解析（解析失败 → undefined，不裸抛）；
 *   - 非 plain object（string/number/array/null）→ `undefined` =「未声明能力面」→
 *     载体落 MINI_AGENT_BASELINE（在载体处 fail-CLOSED，不是 fail-open）；
 *   - 合法容器的**数组轴**做 trim/去重；**畸形轴**（如 `nativeTools: "Bash"`）原样
 *     透传 —— 轴语义的唯一所有者是 `applySessionProfile`，它对畸形白名单轴
 *     fail-closed。在这里把它洗成 `undefined` 会静默翻转成「未声明 = 不覆写」即
 *     fail-OPEN，正是让白名单形同虚设的那个反转。
 */
export function coerceSessionProfile(raw) {
    let v = raw;
    if (typeof v === "string") {
        if (!v.trim())
            return undefined;
        try {
            v = JSON.parse(v);
        }
        catch {
            return undefined;
        }
    }
    if (typeof v !== "object" || v === null || Array.isArray(v))
        return undefined;
    const src = v;
    const out = { ...src };
    for (const axis of SESSION_PROFILE_ARRAY_AXES) {
        const a = src[axis];
        if (Array.isArray(a))
            out[axis] = cleanNameList(a);
    }
    return out;
}
/**
 * 把一份**旧 schema 的** `tools` / `skills` 归一成真正生效的能力面 —— 存量数据进入
 * 白名单世界的**唯一**换算规则（红线 #9）。
 *
 * 两个 producer 共用它，不是巧合而是要求：
 *   1. 本机 sqlite 存量行的一次性迁移（core `agent-capability-migration.ts`）
 *   2. **组织发布池里 #1648 之前写的旧 manifest** 的入库归一（`manifestToProfileInput`）
 * 两者面对的是同一批旧字段、同一个「认不出就丢弃」的取向；各写一份 = 同一个 Agent
 * 在「本机建的」和「管理员发的」两条路上迁出不同能力面。
 *
 * 【住 shared 而非 core 的原因】与 {@link LEAN_GATEABLE_NATIVE_TOOLS} /
 * {@link MINI_AGENT_BASELINE} 同层（都是 SessionProfile 取值域的定义面），且 manifest
 * 解析链整条在 shared —— 依赖方向 `shared ← core` 不允许 shared 反向取 core。
 *
 * 确定性分类（**非模型推导**）—— 一个旧 `tools` 名字只有三种归宿：
 *   1. ∈ {@link LEAN_GATEABLE_NATIVE_TOOLS}  → `nativeTools`
 *   2. ∈ dosia-tools 注册名集合              → `dosiaTools`
 *   3. 都不匹配                              → **丢弃**
 * 旧 `tools` 是模型自由填的 hints（命名混乱、不可信），认不出的名字既不能当原生工具
 * 也不能当 dosia 工具，留着只会把噪音固化成白名单。大小写敏感、全量精确匹配，无模糊/
 * 前缀/别名推断 —— 同一行数据在任何机器上迁移结果完全一致（可审计的前提）。
 *
 * 另两条规则：
 *   - `skills` 直接搬进 `skills`（那是**真白名单**，经 agentSkills 生效，无损搬迁）；
 *   - `mcpServers` / `subagents` → `[]`（保守基线：存量没有任何声明可依据，按「白名单
 *     从零」落最小面，而不是替用户猜他想要哪些）。
 *
 * `tools` 与 `skills` 都为空的行，产出的正是 {@link MINI_AGENT_BASELINE}（五轴全 []）
 * —— 不是特判分支，是同一条规则的自然结果。
 */
export function buildMigratedCapabilities(row, dosiaToolNames) {
    const native = new Set(LEAN_GATEABLE_NATIVE_TOOLS);
    const nativeTools = [];
    const dosiaTools = [];
    for (const raw of row.tools) {
        const name = raw.trim();
        if (!name)
            continue;
        if (native.has(name)) {
            if (!nativeTools.includes(name))
                nativeTools.push(name);
        }
        else if (dosiaToolNames.has(name)) {
            if (!dosiaTools.includes(name))
                dosiaTools.push(name);
        }
        // else: 认不出 → 丢弃（不可信 hint，见上）
    }
    return {
        ...MINI_AGENT_BASELINE,
        skills: [...row.skills],
        dosiaTools,
        nativeTools,
    };
}
/**
 * 校验一份能力面里 `nativeTools` / `dosiaTools` 两轴的名字是否都在权威闭集内。返回**逐轴**
 * 的坏名字 + 可用集；空数组 = 全部合法。`skills` / `mcpServers` / `subagents` 三轴服务端
 * 没有可靠的权威闭集（技能可来自插件、MCP/子 agent 因机器而异），校验它们只会误伤，故
 * 不在此列 —— 它们沿用「客户端 reconcile 认不出即丢弃」的既有语义。
 *
 * 唯一校验规则（红线 #9）：直发端点（`publishOrgAgentDirect`）与其测试共用本函数，禁各自
 * 手抄 `LEAN_GATEABLE_NATIVE_TOOLS.includes`。
 */
export function validateOrgAgentCapabilityNames(capabilities, dosiaToolNames) {
    if (!capabilities)
        return [];
    const issues = [];
    const check = (axis, values, allowed) => {
        if (!Array.isArray(values))
            return;
        const unknownNames = [];
        for (const raw of values) {
            if (typeof raw !== "string")
                continue;
            const name = raw.trim();
            if (!name || allowed.has(name))
                continue;
            if (!unknownNames.includes(name))
                unknownNames.push(name);
        }
        if (unknownNames.length) {
            issues.push({ axis, unknownNames, available: [...allowed].sort() });
        }
    };
    check("nativeTools", capabilities.nativeTools, new Set(LEAN_GATEABLE_NATIVE_TOOLS));
    check("dosiaTools", capabilities.dosiaTools, dosiaToolNames);
    return issues;
}
/**
 * chat.agentId 上的 capabilityRef 绑定前缀（v3 活绑定 scheme）。卡片点击建 chat 时，
 * agentId = `capability:<capabilityRef>`（如 `capability:skill:lark-base`、
 * `capability:plugin:feishu`、`capability:mcp:juguang`、`capability:agent:<id>`）。这是
 * core↔renderer 之间唯一约定的 chat 绑定魔法串（红线 #9：跨生产/消费的序列化标识，唯一
 * build/parse）。真正的角色 Agent 用 `builtin-agent-*` / 用户自建 uuid，绝不与本前缀冲突。
 */
export const CAPABILITY_BINDING_PREFIX = "capability:";
/**
 * 把一个 capabilityRef 串（`skill:x` / `plugin:x` / `mcp:server[#tool]` / `agent:id`，由
 * buildCapabilityRef 产出）编码成 chat.agentId 绑定串。**唯一构建器**（红线 #9）——卡片启动
 * 入口全走它，禁多处手搓 `"capability:" + ...`。
 */
export function capabilityChatBinding(ref) {
    return `${CAPABILITY_BINDING_PREFIX}${ref}`;
}
/**
 * 从 chat.agentId 剥 `capability:` 前缀取 capabilityRef 串；非 capability 绑定（普通 agent
 * id / builtin agent id / 老 `skill-agent:` / 空）返回 null。**唯一解析器**（红线 #9）。
 */
export function parseCapabilityBinding(agentId) {
    if (!agentId || !agentId.startsWith(CAPABILITY_BINDING_PREFIX))
        return null;
    const ref = agentId.slice(CAPABILITY_BINDING_PREFIX.length).trim();
    return ref || null;
}
/**
 * 【退役 scheme·仅读兼容】老 chat.agentId 绑定前缀（`skill-agent:<skillName>`）。v3 已改用
 * `capability:` scheme（capabilityChatBinding）——本前缀不再作**活**绑定写入，仅用于把 DB
 * 里存量老 chat 归一化成 capabilityRef（chatAgentIdToCapabilityRef 内部，红线 #8 例外＝
 * 跨版本数据兼容，非并存第二条活路径）。
 */
export const SKILL_AGENT_BINDING_PREFIX = "skill-agent:";
/**
 * 从老 `skill-agent:` 绑定解出技能名；非老绑定返回 undefined。仅供 chatAgentIdToCapabilityRef
 * 的存量归一化消费（唯一残留读点），renderer/新写路径一律走 capabilityRef。
 */
export function parseSkillAgentBinding(agentId) {
    if (!agentId || !agentId.startsWith(SKILL_AGENT_BINDING_PREFIX))
        return undefined;
    const name = agentId.slice(SKILL_AGENT_BINDING_PREFIX.length).trim();
    return name || undefined;
}
/**
 * chat.agentId → capabilityRef 串的**单一归一化点**（seam 分流 + 频次/引导反查共用）：
 *   - `capability:<ref>`          → `<ref>`（v3 活绑定）
 *   - `skill-agent:<name>`（存量）→ `skill:<name>`（视作 skill 级 capabilityRef，与新口径一致：
 *                                    老技能卡 chat 也走 skill 级收窄——这就是「归一化一行」，
 *                                    不是并存第二条活路径，红线 #8）
 *   - 其它（普通 agent / builtin / 空）→ null
 */
export function chatAgentIdToCapabilityRef(agentId) {
    const cap = parseCapabilityBinding(agentId);
    if (cap)
        return cap;
    const legacySkill = parseSkillAgentBinding(agentId);
    if (legacySkill)
        return `skill:${legacySkill}`;
    return null;
}
/**
 * 从 chat.agentId 解出**技能 bare 名**（仅 `skill:` 类 capabilityRef；含存量 `skill-agent:`
 * 归一化后的 skill 绑定）——供频次索引（work-items）/ 引导卡（ChatWindow）反查技能。
 * plugin/mcp/agent 类绑定或非绑定 → undefined（它们不参与技能级频次/引导）。
 */
export function skillNameFromChatAgentId(agentId) {
    const ref = chatAgentIdToCapabilityRef(agentId);
    const SKILL = "skill:";
    if (!ref || !ref.startsWith(SKILL))
        return undefined;
    const name = ref.slice(SKILL.length).trim();
    return name || undefined;
}
/**
 * F7 Codex 会话谓词——「当前 chat 是否 Codex 编码助手场景会话」的**单一判定点**（红线 #9），
 * UI（模型选择器分叉 / 入口卡）与 desktop（委派模型注入）两侧共用，禁各写一份字符串前缀比对。
 * 判据：agentId 归一化出的 capabilityRef 是 `codex:` scheme（任意 `codex:<x>`，含规范
 * {@link CODEX_CAPABILITY_REF}）。非绑定 / 其它 scheme → false。
 */
export function isCodexChatAgentId(agentId) {
    const ref = chatAgentIdToCapabilityRef(agentId);
    return !!ref && (ref === CODEX_CAPABILITY_REF || ref.startsWith("codex:"));
}
//# sourceMappingURL=skill-agent.js.map